import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Schema } from "rsuite";
import { Icon } from "@rsuite/icons";
import { Save, ImportStorage } from "@rsuite/icons";
import RawPacketsFilters from "./RawPacketsFilters";
import RawPacketsGroupTable from "./RawPacketsGroupTable";
import DarkTable from "components/Table/DarkTable";
import Panel from "components/base/Panel";
import {
    PageContainer
} from "components/Page/PageComponents";
import { useData } from "hooks/useData";
import { callsMock } from "mocks/callsMock";
import { handleDownload, isObjectsEqual } from "utils";
import { lastMinutesToEndOfDayUTC, toBackendUTCString } from "utils/time";
import { RawPacketFilters, rawPacketsFormModel } from "types/filterConfig";
import { useGetRawPacketsQuery, useOrderDumpMutation, useDownloadDumpMutation } from "store/apiSlice";

import TableActionButton from "components/Button/TableActionButton";

interface RawPacketsPageProps {
    initFilters: RawPacketFilters
};

const RawPacketsPage: React.FC<RawPacketsPageProps> = ({ initFilters }) => {
    const [refreshKey, setRefreshKey] = useState<number | null>(null);
    const [disabledRawPackets, setDisabledRawPackets] = useState<string[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    const [formError, setFormError] = useState<Record<string, string>>({});
  
    const filterKeys: (keyof RawPacketFilters)[] = [
        "time_from",
        "time_to",
        "ip_src",
        "ip_dst",
        "call_id",
        "method_code"
    ];

    
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let mutated = false;
    
        // підставляємо інші ключі з initFilters, якщо відсутні
        for (const k of filterKeys) {
            if (!params.has(k) && initFilters[k] !== undefined) {
                params.set(k, String(initFilters[k] ?? ""));
                mutated = true;
            }
        }
    
        // якщо немає time_*, виставляємо останні 5 хв у UTC (обрізані до хвилини)
        const hasTimeFrom = params.has("time_from");
        const hasTimeTo   = params.has("time_to");
        if (!hasTimeFrom || !hasTimeTo) {
            const { from, to } = lastMinutesToEndOfDayUTC(5);
            params.set("time_from", toBackendUTCString(from));
            params.set("time_to",   toBackendUTCString(to));
            mutated = true;
        }
    
        if (mutated) {
            setSearchParams(params, { replace: true });
        }
    }, []);

    const savedFilters = useMemo<RawPacketFilters>(() => {
        const filters: RawPacketFilters = { ...initFilters };

        filterKeys.forEach((key) => {
            const value = searchParams.get(key);
            if (value !== null) {
                filters[key] = value;
            }
        });

        const validation = rawPacketsFormModel.check(filters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        return filters;
    }, [initFilters, searchParams]);

    useEffect(() => {
        setFilters(savedFilters);
    }, [savedFilters]);

    const [filters, setFilters] = useState<RawPacketFilters>(savedFilters);

    const skipQuery = useMemo(() => {
        return isObjectsEqual(filters, initFilters) && refreshKey === null;
    }, [filters, initFilters, refreshKey]);

    const {
        data: calls,
        isLoading,
        isFetching,
        error
    } = useData(
        useGetRawPacketsQuery,
        callsMock,
        {
            time_from: filters.time_from,
            time_to: filters.time_to,
            ip_src: filters.ip_src,
            ip_dst: filters.ip_dst,
            call_id: filters.call_id,
            method_code: filters.method_code,
            refreshKey
        },
        skipQuery
    );

    const [orderDump] = useOrderDumpMutation();
    const [downloadDump] = useDownloadDumpMutation();

    const columns = useMemo(() => [
        { label: "Start Time", dataKey: "time", flexGrow: 1, align: "left", resizable: false },
        { label: "Calling Number", dataKey: "number_from", flexGrow: 1, align: "left", resizable: false },
        { label: "Called Number", dataKey: "number_to", flexGrow: 1, align: "left", resizable: false },
        {
            label: "Incoming IP",
            dataKey: "ip_src",
            value: ({ ip_src, port_src }: any) => {
                return `${ip_src}:${port_src}`;
            },
            minWidth: 180,
            align: "left",
            resizable: false
        },
        {
            label: "Outgoing IP",
            dataKey: "ip_dst",
            value: ({ ip_dst, port_dst }: any) => {
                return `${ip_dst}:${port_dst}`;
            },
            minWidth: 180,
            align: "left",
            resizable: false
        },
        {
            label: "Type",
            dataKey: "type",
            width: 72,
            align: "left",
            resizable: false
        },
        {
            label: "Method/Code",
            dataKey: "method_code",
            flexGrow: 1,
            align: "left",
            resizable: false
        },
        {
            label: "Action",
            dataKey: "action",
            width: 145,
            align: "center",
            value: ({ call_id, dump_filename, dump_completed = 0 }: any) => {
                const disabledOrderDump = disabledRawPackets.includes(call_id);

                return <>
                    {dump_completed
                        ? <TableActionButton onClick={async () => {
                            const blob = await downloadDump({ dump_filename }).unwrap();
                            handleDownload(dump_filename, blob);
                        }}>
                            <Icon as={Save} />
                            <span>Download</span>
                        </TableActionButton>
                        : <TableActionButton
                            disabled={disabledOrderDump} 
                            onClick={() => { 
                                orderDump({ call_id });
                                setDisabledRawPackets((prev) => [...prev, call_id]);
                            }}
                        >
                            <Icon as={ImportStorage} />
                            <span>Order dump</span>
                        </TableActionButton>
                    }
                </>;
            }
        }
    ], [disabledRawPackets, orderDump, downloadDump]);

    const [filtersExpanded, setFiltersExpanded] = useState(true);

    const [currentCallId, setCurrentCallId] = useState(null);
    const [currentRawPacketData, setCurrentRawPacketData] = useState(null);

    const loading = isLoading || isFetching;

    useEffect(() => {
        if (calls && calls.length) {
            const firstCall = calls[0];
            setCurrentCallId(firstCall.call_id);
            setCurrentRawPacketData(firstCall);
        } else {
            setCurrentCallId(null);
            setCurrentRawPacketData(null);
        }
    }, [calls]);

    return (
        <PageContainer>
            <RawPacketsFilters
                expanded={filtersExpanded}
                model={rawPacketsFormModel}
                loading={loading}
                filters={filters}
                formError={formError}
                setFilters={setFilters}
                setFormError={setFormError}
                setRefreshKey={setRefreshKey}
                setSearchParams={setSearchParams}
            />
            <Panel noStyles noEndPadding>
                <DarkTable
                    data={calls}
                    columns={columns}
                    loading={loading}
                    error={error}
                    showSkeleton
                    // showError
                    rowHeight={40}
                    headerHeight={40}
                    height={320}
                    bordered
                    cellBordered

                    onRowClick={({ call_id, ...rowData }) => {
                        setCurrentCallId(call_id);
                        setCurrentRawPacketData(rowData);
                    }}
                    rowClassName={(props: any, rowIndex: number) => {
                        if (props && props.call_id === currentCallId) {
                            return "current_row";
                        }
                        return "";
                    }}
                />

                <RawPacketsGroupTable
                    callId={currentCallId}
                    rawPacketData={currentRawPacketData}
                    rawPacketsLoading={loading}
                />
            </Panel>
        </PageContainer>
    );
};

export default RawPacketsPage; 